import React from "react";
import gs, { colors } from "gstyles";
import cx from "classnames";
import { useT } from "providers/I18nProvider";

interface PaginationProps {
  pagination: {
    page: number;
    total: number;
    limit: number;
    onPreviousPage: () => void;
    onNextPage: () => void;
    onLoadPage: (page: number) => void;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  } | null;
}

const Pagination = ({ pagination }: PaginationProps) => {
  const t = useT();
  if (!pagination) return null;

  const {
    page,
    total,
    limit,
    onPreviousPage,
    onNextPage,
    onLoadPage,
    hasNextPage,
    hasPreviousPage,
  } = pagination;

  if (total <= limit) return null;
  const totalPages = Math.ceil(total / limit);
  const currentPage = page;

  // Tạo array các trang để hiển thị (hiện tại ±2 trang)
  const getVisiblePages = () => {
    const pages = [];

    // Thêm 2 trang trước
    for (let i = Math.max(1, currentPage - 2); i < currentPage; i++) {
      pages.push(i);
    }

    // Thêm trang hiện tại
    pages.push(currentPage);

    // Thêm 2 trang sau
    for (
      let i = currentPage + 1;
      i <= Math.min(totalPages, currentPage + 2);
      i++
    ) {
      pages.push(i);
    }

    return pages;
  };

  return (
    <div className="flex items-center justify-center gap-3 p-4">
      {/* Previous Button */}
      <button
        onClick={onPreviousPage}
        disabled={!hasPreviousPage}
        aria-label={t("home.paginationPrev")}
        className={cx(
          "flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200",
          hasPreviousPage
            ? "bg-primary hover:bg-primary/90 cursor-pointer"
            : "bg-gray-300 cursor-not-allowed opacity-50"
        )}
      >
        {gs.icons({
          name: "arrow-right",
          size: 16,
          color: colors.white,
          style: { transform: "rotate(180deg)" },
        })}
      </button>

      {/* Page Numbers */}
      <div className="flex items-center gap-2">
        {getVisiblePages().map((pageNum) => (
          <button
            key={pageNum}
            onClick={() => onLoadPage(pageNum)}
            className={cx(
              "flex items-center justify-center w-8 h-8 rounded-lg text-sm font-medium transition-all duration-200 text-black01",
              pageNum === currentPage
                ? "bg-primary text-white shadow-md"
                : "bg-gray-100 hover:bg-gray-200"
            )}
          >
            {pageNum}
          </button>
        ))}
      </div>

      {/* Next Button */}
      <button
        onClick={onNextPage}
        disabled={!hasNextPage}
        aria-label={t("home.paginationNext")}
        className={cx(
          "flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200",
          hasNextPage
            ? "bg-primary hover:bg-primary/90 cursor-pointer"
            : "bg-gray-300 cursor-not-allowed opacity-50"
        )}
      >
        {gs.icons({
          name: "arrow-right",
          size: 16,
          color: colors.white,
        })}
      </button>
    </div>
  );
};

export default Pagination;
